/**
 * SpawnSystem — Enemy wave spawning
 *
 * Escalating difficulty:
 * - Wave timer decreases over time
 * - More enemies per wave as game progresses
 * - Harder enemy types unlock at higher waves
 * - Spawn from screen edges (random)
 */

export class SpawnSystem {
  constructor(content) {
    this.content = content; // ContentLoader instance
    this._spawnTimer = 0;
    this._waveTimer = 0;
    this._waveDuration = 15; // seconds per wave
    this._baseSpawnInterval = 1.5; // seconds between spawns
    this._enemiesPerSpawn = 1;
    this._maxEnemies = 150; // cap total enemies on screen
  }

  update(dt, entities, engine) {
    if (engine.state.gameOver || engine.state.levelUpPending) return;

    // Wave progression
    this._waveTimer += dt;
    if (this._waveTimer >= this._waveDuration) {
      this._waveTimer = 0;
      engine.state.wave++;

      // Scale difficulty
      this._baseSpawnInterval = Math.max(0.3, 1.5 - engine.state.wave * 0.1);
      this._enemiesPerSpawn = Math.min(8, 1 + Math.floor(engine.state.wave / 2));
      this._maxEnemies = Math.min(300, 150 + engine.state.wave * 20);
    }

    // Spawn timer
    this._spawnTimer += dt;
    const spawnInterval = this._baseSpawnInterval;

    if (this._spawnTimer >= spawnInterval) {
      this._spawnTimer = 0;

      const currentEnemyCount = entities.countTag('enemy');
      if (currentEnemyCount >= this._maxEnemies) return;

      for (let i = 0; i < this._enemiesPerSpawn; i++) {
        this._spawnEnemy(entities, engine);
      }
    }
  }

  _spawnEnemy(entities, engine) {
    const def = this.content.randomEnemy();
    if (!def) return;

    // Spawn position: random edge of screen
    const pos = this._randomEdgePosition(engine.width, engine.height);

    const id = entities.create();

    entities.addComponent(id, 'position', {
      x: pos.x,
      y: pos.y,
      angle: 0,
    });

    entities.addComponent(id, 'velocity', { vx: 0, vy: 0 });

    entities.addComponent(id, 'health', {
      current: def.hp + Math.floor(engine.state.wave * 0.5), // scale HP with wave
      max: def.hp + Math.floor(engine.state.wave * 0.5),
    });

    entities.addComponent(id, 'render', {
      shape: def.shape || 'triangle',
      size: def.size || 12,
      color: def.color || '#ff0040',
      glow: true,
    });

    entities.addComponent(id, 'behavior', {
      type: def.behavior || 'chase',
      speed: def.speed + engine.state.wave * 2, // slight speed increase per wave
      dashTimer: 0,
      dashInterval: 2.5 + Math.random(),
      orbitRadius: 120 + Math.random() * 80,
      orbitAngle: Math.random() * Math.PI * 2,
      shootTimer: 0,
      shootInterval: 2 + Math.random(),
    });

    entities.addTag(id, 'enemy');
  }

  _randomEdgePosition(w, h) {
    const margin = 30;
    const edge = Math.floor(Math.random() * 4);
    switch (edge) {
      case 0: return { x: -margin, y: Math.random() * h };                // left
      case 1: return { x: w + margin, y: Math.random() * h };            // right
      case 2: return { x: Math.random() * w, y: -margin };                // top
      case 3: return { x: Math.random() * w, y: h + margin };            // bottom
      default: return { x: -margin, y: Math.random() * h };
    }
  }
}
